import { isAxiosError } from 'axios';
import { BaseAttioService } from './BaseAttioService';
import { AnalystNote, AttioNote, AttioNotesQueryResponse, CreateAnalystNoteRequest, CreateFulfillmentNoteRequest, CreateNoteRequest, CreateWorkboardTaskOccurrenceNoteRequest, UpdateAnalystNoteRequest, UpdateFulfillmentNoteRequest, UpdateNoteRequest } from '../attio.types';

export type WorkboardTaskOccurrenceLookup = {
  workboardTaskId: number;
  occurrenceDate: string;
};

export const getWorkboardTaskOccurrenceLookupKey = ({ workboardTaskId, occurrenceDate }: WorkboardTaskOccurrenceLookup) =>
  `${workboardTaskId}:${occurrenceDate}`;

// Service-specific types
export interface AttioNoteData {
  content: string;
  created_at: string;
  title: string;
}

export interface AnalystNoteHistoryItem {
  id: number;
  content: string;
  author_id: number;
  author_email: string;
  author_name: string;
  author_picture_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export class AttioNotesService extends BaseAttioService {
  constructor() {
    super('AttioNotesService');
  }

  /**
   * Get the newest (latest) analyst note for a given reference.
   * Uses the backend bulk endpoint because it accepts reference_type + id in the request body.
   */
  async getLatestNoteForReference(
    referenceType: 'at' | 'fu',
    referenceId: string | number
  ): Promise<{ id: number; content: string } | null> {
    const requestBody =
      referenceType === 'at'
        ? { attio_valuation_ids: [referenceId], reference_type: 'at' }
        : { reference_ids: [referenceId], reference_type: 'fu' };

    // Bulk endpoint returns: { [referenceIdAsString]: noteObject | null }
    const response = await this.post<Record<string, { id?: number; content?: string } | null>>(
      this.endpoints.retrieveBulkNotes,
      requestBody
    );

    const key = String(referenceId);
    const note = response?.[key];
    if (!note) {
      return null;
    }

    return {
      id: note.id as number,
      content: (note.content as string) ?? '',
    };
  }

  /**
   * Get newest-first history of analyst notes for a given reference.
   * Uses POST /api/analyst-notes/bulk-history/.
   */
  async getNotesHistoryForReference(
    referenceType: 'at' | 'fu',
    referenceId: string | number
  ): Promise<AnalystNoteHistoryItem[]> {
    const requestBody =
      referenceType === 'at'
        ? { attio_valuation_ids: [referenceId], reference_type: 'at' }
        : { reference_ids: [referenceId], reference_type: 'fu' };

    const response = await this.post<Record<string, AnalystNoteHistoryItem[]>>(
      this.endpoints.retrieveBulkNotesHistory,
      requestBody
    );

    const key = String(referenceId);
    return response?.[key] ?? [];
  }

  async getNotesHistoryForWorkboardTaskOccurrence(
    lookup: WorkboardTaskOccurrenceLookup
  ): Promise<AnalystNoteHistoryItem[]> {
    const response = await this.post<Record<string, AnalystNoteHistoryItem[]>>(
      this.endpoints.retrieveBulkNotesHistory,
      {
        reference_type: 'wo',
        reference_ids: [],
        workboard_task_occurrence_keys: [
          {
            workboard_task_id: lookup.workboardTaskId,
            occurrence_date: lookup.occurrenceDate,
          },
        ],
      }
    );

    return response?.[getWorkboardTaskOccurrenceLookupKey(lookup)] ?? [];
  }
  /**
   * Get the analyst note for a valuation record.
   * Returns null when no DB record exists (404), or the note object when it does
   * (even if content is empty). Use null to decide POST vs PUT.
   */
  async getAnalystNote(recordId: string): Promise<AnalystNote | null> {
    try {
      return await this.get<AnalystNote>(
        this.endpoints.retrieveUserNoteByValuationId(recordId)
      );
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      // eslint-disable-next-line no-console
      console.warn('Error fetching analyst note:', error);
      return null;
    }
  }


  /**
   * Create a new analyst note for a valuation record
   */
  async createAnalystNote(request: CreateAnalystNoteRequest): Promise<AnalystNote> {
    try {
      const response: AnalystNote = await this.post(
        this.endpoints.createAnalystNote(),
        request
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating analyst note:', error);
      throw error;
    }
  }

  /**
   * Update an existing analyst note for a valuation record
   */
  async updateAnalystNote(recordId: string, request: UpdateAnalystNoteRequest): Promise<AnalystNote> {
    try {
      const response: AnalystNote = await this.put(
        this.endpoints.updateAnalystNote(recordId),
        request
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating analyst note:', error);
      throw error;
    }
  }

  /**
   * Create a new analyst note for a fulfillment record
   */
  async createFulfillmentNote(request: CreateFulfillmentNoteRequest): Promise<AnalystNote> {
    try {
      const response: AnalystNote = await this.post(
        this.endpoints.createAnalystNote(),
        request
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating fulfillment note:', error);
      throw error;
    }
  }

  async createWorkboardTaskOccurrenceNote(
    request: CreateWorkboardTaskOccurrenceNoteRequest
  ): Promise<AnalystNote> {
    try {
      const response: AnalystNote = await this.post(this.endpoints.createAnalystNote(), request);
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating workboard task occurrence note:', error);
      throw error;
    }
  }

  /**
   * Update an existing analyst note for a fulfillment record
   */
  async updateFulfillmentNote(recordId: string, request: UpdateFulfillmentNoteRequest): Promise<AnalystNote> {
    try {
      const response: AnalystNote = await this.put(
        this.endpoints.updateAnalystNote(recordId),
        request
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating fulfillment note:', error);
      throw error;
    }
  }

  /**
   * Get all notes for a valuation record
   */
  async getAllNotes(recordId: string, parentObject?: string): Promise<AttioNoteData[]> {
    try {
      const rawData: AttioNotesQueryResponse = await this.get(
        this.endpoints.retrieveNotesByRecordIdAndParentObject(recordId, parentObject || 'valuations')
      );
      return AttioNotesService.extractAllNotes(rawData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error fetching all notes:', error);
      return [];
    }
  }

  /**
   * Extract all notes from notes response
   */
  private static extractAllNotes(rawData: AttioNotesQueryResponse): AttioNoteData[] {
    if (!rawData.data || rawData.data.length === 0) {
      return [];
    }

    return rawData.data.map(note => ({
      content: note.content_plaintext || '-',
      created_at: note.created_at,
      title: note.title || 'Untitled Note',
    }));
  }


  /**
   * Get notes for a specific parent object and record ID
   */
  async getNotesForRecord(parentObject: string, parentRecordId: string): Promise<AttioNote[]> {
    try {
      const response = await this.get<AttioNotesQueryResponse>(
        this.endpoints.retrieveNotesByRecordIdAndParentObject(parentRecordId, parentObject)
      );
      return response.data || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Error fetching notes for ${parentObject}:${parentRecordId}:`, error);
      return [];
    }
  }

  /**
   * Get notes from multiple records with prefixes
   */
  async getNotesForValuation(records: Array<{ parentObject: string; parentRecordId: string; prefix: string }>): Promise<AttioNote[]> {
    try {
      // Fetch all notes in parallel
      const notesPromises = records.map(record => 
        this.getNotesForRecord(record.parentObject, record.parentRecordId)
          .then(notes => notes.map(note => ({
            ...note,
            title: `${record.prefix}: ${note.title}`,
          })))
      );

      const allNotesResponses = await Promise.allSettled(notesPromises);

      // Combine all successful responses
      const allNotes: AttioNote[] = allNotesResponses.flatMap(result =>
        result.status === 'fulfilled' ? result.value : []
      );

      // Sort all notes by creation date (newest first)
      const sortedNotes = allNotes.toSorted((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return sortedNotes;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching Attio notes for valuation:', error);
      throw error;
    }
  }

  /**
   * Create or update a note using the unified interface
   */
  async createOrUpdateNote(request: CreateNoteRequest, hasExistingNote: boolean = false, existingNoteId?: string): Promise<AnalystNote> {
    try {
      if (hasExistingNote) {
        if (existingNoteId) {
          // Preferred: update by note ID
          return await this.updateNote({ id: existingNoteId, content: request.content });
        }
        if (request.attio_valuation_id) {
          // Fallback: update by valuation record ID when note ID is not available
          return await this.updateAnalystNote(request.attio_valuation_id, {
            content: request.content,
            reference_type: 'at',
          });
        }
      }
      // No existing note: create
      return await this.createNote(request);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating or updating note:', error);
      throw error;
    }
  }

  /**
   * Create a new note using the unified interface
   */
  async createNote(request: CreateNoteRequest): Promise<AnalystNote> {
    try {
      if (request.reference_type === 'at' && request.attio_valuation_id) {
        const createRequest: CreateAnalystNoteRequest = {
          attio_valuation_id: request.attio_valuation_id,
          content: request.content,
          reference_type: 'at',
        };
        return await this.createAnalystNote(createRequest);
      }
      if (request.reference_type === 'fu' && request.fulfillment) {
        const createRequest: CreateFulfillmentNoteRequest = {
          fulfillment: request.fulfillment,
          content: request.content,
          reference_type: 'fu',
        };
        return await this.createFulfillmentNote(createRequest);
      }
      if (request.reference_type === 'wo' && request.workboard_task_id && request.occurrence_date) {
        const createRequest: CreateWorkboardTaskOccurrenceNoteRequest = {
          workboard_task_id: request.workboard_task_id,
          occurrence_date: request.occurrence_date,
          content: request.content,
          reference_type: 'wo',
        };
        return await this.createWorkboardTaskOccurrenceNote(createRequest);
      }
      throw new Error('Invalid request: missing required fields for note creation');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating note:', error);
      throw error;
    }
  }

  /**
   * Update an existing note using the unified interface
   */
  async updateNote(request: UpdateNoteRequest): Promise<AnalystNote> {
    try {
      // Update note using the note ID directly
      const response: AnalystNote = await this.put(
        this.endpoints.updateAnalystNote(request.id),
        { content: request.content }
      );
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating note:', error);
      throw error;
    }
  }

  /**
   * Delete a note by note ID
   */
  async deleteNote(noteId: number): Promise<void> {
    try {
      await this.client.delete(this.endpoints.deleteAnalystNote(noteId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting note:', error);
      throw error;
    }
  }
}
